import { isDevMode, NgModule, provideBrowserGlobalErrorListeners } from '@angular/core';
import { BrowserModule, provideClientHydration, withEventReplay } from '@angular/platform-browser';
import { providePrimeNG } from 'primeng/config';
import { MainRoutingModule } from './main-routing.module';
import { AppComponent } from './app.component';
import Aura from '@primeng/themes/aura';
import { environment } from '../environments/environment';
import { ReactiveFormsModule } from '@angular/forms';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { PrimengModule } from './app/shared/primeng/primeng.module';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { AuthConfigModule } from './open_id/auth-config.module';
import myLocaleIt from '@angular/common/locales/it';
import myLocaleEn from '@angular/common/locales/en';
import { registerLocaleData } from '@angular/common';
import { NotFoundComponent } from './errors/not-found/not-found.component';
import { GenericFailComponent } from './errors/generic-fail/generic-fail.component';
import { LoginFailComponent, LoginSuccessComponent, LoginErrorComponent } from './open_id';
import * as authEffects from './app/store/auth/auth.effects';
import * as commonEffects from './app/store/common/common.effects';
import * as agentEffects from './app/store/agent/agent.effects';
import * as cronJobEffects from './app/store/cronJob/cronJob.effects';
import * as usersEffect from './app/store/users/users.effects';
import * as fileManagerEffect from './app/store/file-manager/file-manager.effects';
import * as timelineEffect from './app/store/timeline/timeline.effects';
import * as infoCardEffect from './app/store/info-card/info-card.effects';
import * as companiesEffect from './app/store/companies/companies.effects';
import * as alertEffects from './app/store/alert/alert.effects';
import * as brandsEffects from './app/store/brands/brand-effects';
import * as snmpVaultEffects from './app/store/snmp-vault/snmp-vault.effect';
import * as printerModelEffect from './app/store/printer-model/printer-model.effects';
import * as printerEffect from './app/store/printer/printer.effect';
import * as productsEffect from './app/store/product/product.effects';
import * as metricEffect from './app/store/metric/metric.effects';
import * as thresholdEffect from './app/store/threshold/threshold.effect';
import * as emailEventRuleEffects from './app/store/email-event-rule/email-event-rule.effects';
import * as emailReportRuleEffects from './app/store/email-report-rule/email-report-rule.effects';
import * as accountEffects from './app/store/account/account.effects';
import * as terminalEffects from './app/store/terminal/terminal.effect';
import { mainReducer } from './app/store/main/main.reducer';
import { commonReducer } from './app/store/common/common.reducer';
import { usersReducer } from './app/store/users/users.reducer';
import { companiesReducer } from './app/store/companies/companies.reducer';
import { fileManagerReducer } from './app/store/file-manager/file-manager.reducers';
import { timelineReducer } from './app/store/timeline/timeline.reducer';
import { infoCardReducer } from './app/store/info-card/info-card.reducer';
import { agentReducer } from './app/store/agent/agent.reducer';
import { brandsReducer } from './app/store/brands/brand-reducer';
import { PipesModule } from './app/shared/pipes/pipes.module';
import { snmpVaultReducer } from './app/store/snmp-vault/snmp-vault.reducer';
import { printerModelReducer } from './app/store/printer-model/printer-model.reducer';
import { cronJobReducer } from './app/store/cronJob/cronJob.reducer';
import { printerReducer } from './app/store/printer/printer.reducer';
import { productsReducer } from './app/store/product/product.reducer';
import { zetraErrorInterceptor } from './app/interceptors/zetra-error.interceptor';
import { alertReducer } from './app/store/alert/alert.reducer';
import { metricReducer } from './app/store/metric/metric.reducer';
import { thresholdReducer } from './app/store/threshold/threshold.reducer';
import { JWT_OPTIONS, JwtHelperService } from '@auth0/angular-jwt';
import { authInterceptor } from 'angular-auth-oidc-client';
import { TimezoneInterceptor } from './app/interceptors/timezone.interceptor';
import { provideApiConfiguration } from './api/api-configuration';
import { weekPickerReducer } from './app/store/week-picker/week-picker.reducer';
import { emailEventRuleReducer } from './app/store/email-event-rule/email-event-rule.reducer';
import { emailReportRuleReducer } from './app/store/email-report-rule/email-report-rule.reducer';
import { accountReducer } from './app/store/account/account.reducer';
import { terminalReducer } from './app/store/terminal/terminal.reducer';
import { NgxMaskModule } from 'ngx-mask';
import { NgApexchartsModule } from 'ng-apexcharts';
import { authenticationInterceptor } from './app/interceptors/authentication.interceptor';
import { licenseInterceptor } from './app/interceptors/license.interceptor';

registerLocaleData(myLocaleIt, 'it');
registerLocaleData(myLocaleEn, 'en');

@NgModule({
  declarations: [
    AppComponent,
    NotFoundComponent,
    LoginFailComponent,
    LoginSuccessComponent,
    LoginErrorComponent,
    GenericFailComponent,
    // VncViewerComponent,
  ],
  imports: [
    BrowserModule,
    MainRoutingModule,
    ReactiveFormsModule,
    AuthConfigModule,
    PrimengModule,
    PipesModule,
    NgApexchartsModule,
    StoreModule.forRoot({
      main: mainReducer,
      common: commonReducer,
      users: usersReducer,
      companies: companiesReducer,
      fileManager: fileManagerReducer,
      timeline: timelineReducer,
      infoCard: infoCardReducer,
      agent: agentReducer,
      brands: brandsReducer,
      snmpVault: snmpVaultReducer,
      printerModel: printerModelReducer,
      cronJob: cronJobReducer,
      printer: printerReducer,
      products: productsReducer,
      alert: alertReducer,
      metric: metricReducer,
      threshold: thresholdReducer,
      weekPicker: weekPickerReducer,
      emailEventRule: emailEventRuleReducer,
      emailReportRule: emailReportRuleReducer,
      account: accountReducer,
      terminal: terminalReducer,
    }),
    NgxMaskModule.forRoot(),
    EffectsModule.forRoot([
      authEffects,
      commonEffects,
      usersEffect,
      companiesEffect,
      fileManagerEffect,
      timelineEffect,
      infoCardEffect,
      agentEffects,
      brandsEffects,
      snmpVaultEffects,
      printerModelEffect,
      cronJobEffects,
      printerEffect,
      productsEffect,
      alertEffects,
      metricEffect,
      thresholdEffect,
      emailEventRuleEffects,
      emailReportRuleEffects,
      accountEffects,
      terminalEffects,
    ]),
    StoreDevtoolsModule.instrument({ maxAge: 25, logOnly: !isDevMode() }),
  ],
  providers: [
    { provide: JWT_OPTIONS, useValue: JWT_OPTIONS },
    JwtHelperService,
    provideApiConfiguration(environment.baseUrl),
    providePrimeNG({
      theme: {
        preset: Aura,
        options: {
          darkModeSelector: '.darkmode',
        },
      },
    }),
    provideHttpClient(
      withFetch(),
      withInterceptors([
        authInterceptor(),
        TimezoneInterceptor,
        zetraErrorInterceptor,
        authenticationInterceptor,
        licenseInterceptor,
      ]),
    ),
    provideBrowserGlobalErrorListeners(),
    provideClientHydration(withEventReplay()),
  ],
  bootstrap: [AppComponent],
})
export class MainModule {}
